<template>
  <v-flex class="py-0 pr-3 job mb-3 cardBackground rounded-3 elevation-2" style="position: relative" xs12
    :class="{ sm: $vuetify.breakpoint.smAndDown, md: $vuetify.breakpoint.mdAndDown }"
    :data-job-id="id">
    <div v-if="!loading">
      <div class="horiz-left-coloured-bg">
        <v-icon color="secondary" class="mt-0 ml-2 horiz-left-coloured-bg--icon">build</v-icon>
      </div>
      <div class="details">
        <h3 class="pt-0 pb-1">{{ job.jobType }}</h3>
        <div class="contents">
          <UserAvatar avatarOnly :userId="owner.id" status="active" :avatarURL="owner.avatarUrl"
            :name="owner.displayName" size="34px" class="mx-1 mt-2 mr-3" style="float: left"> </UserAvatar>
          <h4 class="mt-0">
            <div class="d-block">
              Started:
              <span style="font-weight: 700">{{ started }}</span>
            </div>
            <div class="d-block">
              Status:
              <span style="font-weight: 700">{{ job.jobStatus }}</span>
            </div>
            <div class="d-inline-block">
              Runtime:
              <span style="font-weight: 700">{{ runTime }}</span>
            </div>
          </h4>
        </div>
      </div>
      <div class="actions">
        <div class="progress">
          <v-progress-circular :rotate="-90" :size="80" :width="15" :value="progressValue" :color="progressColour"
            class="hidden-sm-and-down">
            <h4 style="font-weight: 700">{{ progressValue }}%</h4>
          </v-progress-circular>
        </div>
        <v-flex class="btns">
          <h4 class="mt-0">
            Pass:
            <span class="mr-2" style="font-weight: 700">{{ job.succeeded }}</span>
            Fail:
            <a v-if="canCreateFailureBucket" href="#" class="job-fail-link" @click.prevent="dialogFailures = true">{{ job.failed }}</a>
            <span v-else style="font-weight: 700">{{ job.failed }}</span>
          </h4>
          <v-btn color="secondary" @click="refresh" class="mr-2 ml-0">Refresh</v-btn>
          <v-btn color="error" @click="dialogKill = true" :disabled="!canKill" class="mr-0 ml-0">Kill</v-btn>
          <v-btn color="secondary" class="mr-0" @click="showLog()">Log</v-btn>
          <v-dialog v-model="dialogKill" max-width="500">
            <v-card class="pa-4">
              <v-card-title class="pa-0 pb-2">
                <h2>This job is running, are you sure you want to kill it?</h2>
              </v-card-title>
              <v-card-actions class="ma-0 pa-0 pt-1 d-block">
                <v-btn @click="killJob()" color="error" class="ml-0 mr-2">Yes, Kill this job</v-btn>
                <v-btn @click="dialogKill = false" color="secondary">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogFailures" max-width="500">
            <v-card class="pa-4">
              <v-card-title class="pa-0 pb-2">
                <h2>{{ job.failed }} failed {{ Number(job.failed) === 1 ? "case" : "cases" }}</h2>
              </v-card-title>
              <v-card-text class="pa-0">
                Create a bucket whose advanced query matches these cases.
              </v-card-text>
              <v-card-actions class="ma-0 pa-0 pt-1 d-block">
                <v-btn @click="createFailureBucket()" color="secondary" class="ml-0 mr-2" :disabled="creatingFailureBucket">Create bucket for failures</v-btn>
                <v-btn @click="dialogFailures = false" color="secondary" :disabled="creatingFailureBucket">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogLog" max-width="900">
            <v-card class="pa-4">
              <v-card-title class="pa-0 pb-2">
                <h2>Job Log: {{ job.jobType }}</h2>
              </v-card-title>
              <div v-if="loadingJobLog">Loading</div>
              <div v-else>
                <h3 v-if="logData.length == 0">No log entries found for this job</h3>
                <div v-else class="text-xs-center">
                  <h3>Showing {{ logData.length }} log entries for this job</h3>
                </div>
                <div class="job-log">
                  <div v-for="entry in logData" :key="entry.id" class="job-log-entry">
                    <h4 class="mt-3 mb-1" :class="{ 'error--text': entry.isError }">
                      {{ formatLogTime(entry.createdAt) }} {{ entry.caseTitle || "Job" }} — {{ entry.message }}
                    </h4>
                    <pre v-if="entry.logs && entry.logs.length" class="job-log-output">{{ formatLogLines(entry.logs) }}</pre>
                  </div>
                </div>
              </div>
              <v-card-actions class="ma-0 pa-0 pt-1 d-block">
                <v-btn @click="closeDialog()" color="secondary">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </div>
    </div>
    <div v-else>Loading</div>
  </v-flex>
</template>
<script>
import Helpers from "@/helpers.js"
import UserAvatar from "@/components/Users/UserAvatar.vue"
import { mapState } from "vuex"

import { getJob, getJobFailureCases, getJobStats, killJob as requestKillJob } from "@/provider/jobs"
import { jobsUiDebugLog } from "@/utils/jobsUiDebug"

export default {
  name: "job-item",
  props: {
    id: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object,
      required: true,
    },
    initialJob: {
      type: Object,
      default: null,
    },
  },
  components: { UserAvatar },
  data() {
    return {
      job: this.initialJob || {},
      statsSeq: 0,
    dialogLog: false,
    dialogKill: false,
    dialogFailures: false,
    creatingFailureBucket: false,
    currentPage: 1,
    sortByField: "createdAt",
    sortByDescending: false,
    loading: true,
    loadingJobLog: false,
    logData: [],
    }
  },
  async mounted() {
    jobsUiDebugLog("JobItem:mounted", {
      id: this.id,
      hadInitialJob: !!(this.initialJob && this.initialJob.id),
    })
    if (this.initialJob && this.initialJob.id) {
      this.job = Object.assign({}, this.initialJob)
      jobsUiDebugLog("JobItem:mounted_from_prop", {
        id: this.id,
        jobStatus: this.job.jobStatus,
        succeeded: this.job.succeeded,
        failed: this.job.failed,
        currentStep: this.job.currentStep,
        totalSteps: this.job.totalSteps,
      })
    } else {
      await this.refresh()
    }
    this.loading = false
    if (this.isRunning) {
      jobsUiDebugLog("JobItem:hydrate_stats", { id: this.id, reason: "pending_or_running" })
      void this.refresh()
    }
  },
  computed: {
    ...mapState({
      users: (state) => state.users.all,
      timePref: (state) => (state && state.currentUser && state.currentUser.me && state.currentUser.me.preferences && state.currentUser.me.preferences.dateTimeFormat ? state.currentUser.me.preferences.dateTimeFormat : "HH:MM, D MMM"),
    }),
    canKill() {
      return this.job.jobStatus !== "Completed" && this.job.jobStatus !== "Failed" && this.job.jobStatus !== "Killed"
    },
    canCreateFailureBucket() {
      const finished = this.job.jobStatus === "Completed" || this.job.jobStatus === "Failed"
      return finished && Number(this.job.failed) > 0
    },
    started() {
      return Helpers.formatDate(this.job.createdAt, this.timePref)
    },
    owner() {
      if (this.job.causedBy && this.job.causedBy.id) {
        const user = this.users.find((user) => user.id === this.job.causedBy.id)
        if (user) {
          return user
        }
      }
      return undefined
    },
    runTime() {
      if (this.job.jobStatus === "Completed" || this.job.jobStatus === "Failed" || this.job.jobStatus === "Killed") {
        return Helpers.calculateTimeDifference(this.job.createdAt, this.job.updatedAt)
      } else {
        return Helpers.calculateTimeDifferenceFromNow(this.job.createdAt)
      }
    },
    isRunning() {
      return this.job.jobStatus === "Running" || this.job.jobStatus === "Pending"
    },
    logHeaders() {
      return [
        { text: "Timestamp", value: "createdAt", align: "left", sortable: true },
        { text: "Case", value: "caseTitle", align: "left", sortable: true },
        { text: "Message", value: "message", align: "left", sortable: false },
        { text: "Error", value: "isError", align: "right", sortable: true },
      ]
    },
    pagination: {
      get() {
        return {
          descending: this.sortByDescending,
          page: this.currentPage,
          rowsPerPage: this.currentUser.preferences.pageSize,
          sortBy: this.sortByField,
          totalItems: this.job.totalSteps,
        }
      },
      set(newPagination) {
        this.currentPage = newPagination.page
        this.sortByField = newPagination.sortBy
        this.sortByDescending = newPagination.descending
        return this.$store.dispatch("getJobLog", { jobId: this.job.id, page: this.currentPage - 1, pageSize: newPagination.rowsPerPage, sortBy: this.sortByField, descending: this.sortByDescending })
      },
    },
    taskIcon() {
      switch (this.job.jobType) {
        case "Reindex Case Template":
        case "Reindex All Case Templates":
        case "Reindex Documents":
          return "sort-numeric-down"
        case "Export":
          return "file-export"
        case "Import":
          return "file-import"
        default:
          return "wrench"
      }
    },
    progressColour() {
      switch (this.job.jobStatus) {
        case "Completed":
          return "success"
        case "Running":
          return "tertiary"
        case "Pending":
          return "primary"
        case "Failed":
        case "Killed":
          return "error"
      }
      return "tertiary"
    },
    progressValue() {
      switch (this.job.jobStatus) {
        case "Completed":
          return 100
        case "Failed":
        case "Killed":
          return 0
        default:
          if (this.job.totalSteps > 0) {
            const result = Math.round((this.job.currentStep / this.job.totalSteps) * 100)
            return result
          } else {
            return 0
          }
      }
    },
  },
  methods: {
    formatDate(date) {
      return Helpers.formatDate(date, this.timePref)
    },
    formatLogTime(value) {
      if (value == null || value === "") return ""
      let date
      if (typeof value === "number") {
        date = new Date(value < 1e12 ? value * 1000 : value)
      } else if (typeof value === "string" && /^[0-9]+$/.test(value)) {
        const numeric = Number(value)
        date = new Date(numeric < 1e12 ? numeric * 1000 : numeric)
      } else {
        date = new Date(value)
      }
      if (isNaN(date.getTime())) return String(value)
      const pad = function (n, width) {
        const text = String(n)
        return text.length >= width ? text : new Array(width - text.length + 1).join("0") + text
      }
      return pad(date.getHours(), 2) + ":" + pad(date.getMinutes(), 2) + ":" + pad(date.getSeconds(), 2) + "." + pad(date.getMilliseconds(), 3)
    },
    formatLogLines(logs) {
      const self = this
      return (logs || []).map(function (line) {
        const when = line && line.timestamp ? self.formatLogTime(line.timestamp) : ""
        const level = line && line.level ? line.level : ""
        const message = line && line.message ? line.message : ""
        return [when, level, message].filter(function (part) { return part }).join(" ")
      }).join("\n")
    },
    async createFailureBucket() {
      if (this.creatingFailureBucket) {
        return
      }
      this.creatingFailureBucket = true
      try {
        const failureCases = await getJobFailureCases(this.job.id)
        const caseIds = failureCases && failureCases.caseIds ? failureCases.caseIds : []
        const caseTypeId = failureCases && failureCases.caseTypeId
        if (!caseTypeId || !caseIds.length) {
          this.$store.commit("displayFlashMessage", { message: "No failed cases were found for this job", severity: "error" })
          return
        }
        const advancedQuery = caseIds.map(function (id) { return "id:" + id }).join(" OR ")
        const newBucket = await this.$store.dispatch("createBucket", {
          caseTypeId: caseTypeId,
          name: this.job.jobType + " failures " + this.job.id,
          description: "Cases that failed in job " + this.job.id,
          kpi: "current_state",
          advancedQuery: advancedQuery,
          displayGroupByPanel: true,
          showUserPresence: false,
          displayFields: [],
          criteria: [],
        })
        if (newBucket && newBucket.id) {
          this.dialogFailures = false
          this.$router.push({
            name: "Bucket",
            params: { case_type_id: String(caseTypeId), bucket_id: String(newBucket.id) },
          })
        }
      } catch (err) {
        const message = err && err.message ? err.message : "Could not create the bucket"
        this.$store.commit("displayFlashMessage", { message: message, severity: "error" })
      } finally {
        this.creatingFailureBucket = false
      }
    },
    async killJob() {
      this.dialogKill = false
      try {
        const result = await requestKillJob(this.job.id)
        await this.refresh()
        if (result && result.error) {
          this.$store.commit("displayFlashMessage", { message: result.message || "Failed to kill job", severity: "error" })
        }
      } catch (err) {
        this.$store.commit("displayFlashMessage", { message: err.message || "Failed to kill job", severity: "error" })
      }
    },
    closeDialog() {
      this.mounted = false
      this.dialogLog = false
    },
    async showLog() {
      this.loadingJobLog = true
      this.dialogLog = true
      await this.$store.dispatch("clearJobLog")
      this.logData = await this.$store.dispatch("getJobLog", { jobId: this.job.id, page: this.currentPage - 1, pageSize: 100000, sortBy: this.sortByField, descending: this.sortByDescending })
      this.loadingJobLog = false
    },
    noteLiveStats() {
      this.statsSeq += 1
    },
    applyJobStatsSnapshot(snap) {
      if (!snap) {
        return
      }
      jobsUiDebugLog("JobItem:applyJobStatsSnapshot", {
        id: this.id,
        jobStatus: snap.jobStatus,
        succeeded: snap.succeeded,
        failed: snap.failed,
        countersFromRedis: snap.countersFromRedis,
        currentStep: snap.currentStep,
        totalSteps: snap.totalSteps,
      })
      this.job = Object.assign({}, this.job, {
        id: snap.id,
        jobType: snap.jobType,
        jobStatus: snap.jobStatus,
        currentStep: snap.currentStep,
        totalSteps: snap.totalSteps,
        succeeded: snap.succeeded,
        failed: snap.failed,
        updatedAt: snap.updatedAt !== undefined && snap.updatedAt !== null ? snap.updatedAt : this.job.updatedAt,
        createdAt: snap.createdAt !== undefined && snap.createdAt !== null ? snap.createdAt : this.job.createdAt,
      })
    },
    /** Stats (counters, steps, status) always from `jobStats` so the server merges live Redis; `job` only for owner when needed. */
    async refresh() {
      jobsUiDebugLog("JobItem:refresh:start", { id: this.id })
      const seqAtStart = this.statsSeq
      let snap = null
      try {
        snap = await getJobStats(this.id)
      } catch (err) {
        console.error("getJobStats failed", err)
        jobsUiDebugLog("JobItem:refresh:getJobStats_throw", {
          id: this.id,
          message: err && err.message ? err.message : String(err),
        })
      }
      if (this.statsSeq !== seqAtStart) {
        jobsUiDebugLog("JobItem:refresh:stale", { id: this.id, seqAtStart: seqAtStart, statsSeq: this.statsSeq })
        return
      }
      if (snap) {
        this.applyJobStatsSnapshot(snap)
        jobsUiDebugLog("JobItem:refresh:from_jobStats", { id: this.id })
      } else {
        try {
          this.job = await getJob(this.id)
          jobsUiDebugLog("JobItem:refresh:fallback_getJob", { id: this.id })
        } catch (err2) {
          console.error("getJob failed", err2)
          jobsUiDebugLog("JobItem:refresh:getJob_throw", {
            id: this.id,
            message: err2 && err2.message ? err2.message : String(err2),
          })
        }
        return
      }
      if (!(this.job.causedBy && this.job.causedBy.id)) {
        try {
          const full = await getJob(this.id)
          if (full && full.causedBy) {
            this.job = Object.assign({}, this.job, { causedBy: full.causedBy })
          }
        } catch (err3) {
          console.error("getJob (causedBy) failed", err3)
        }
      }
      jobsUiDebugLog("JobItem:refresh:done", {
        id: this.id,
        jobStatus: this.job.jobStatus,
        succeeded: this.job.succeeded,
        failed: this.job.failed,
      })
    }
  },
  watch: {
    initialJob: {
      handler(val) {
        if (!val || val.id === undefined || val.id === null) {
          return
        }
        if (!this.job || !this.job.id) {
          return
        }
        if (Number(val.id) !== Number(this.job.id)) {
          return
        }
        const before = {
          succeeded: this.job.succeeded,
          failed: this.job.failed,
          jobStatus: this.job.jobStatus,
          currentStep: this.job.currentStep,
        }
        this.noteLiveStats()
        this.job = Object.assign({}, this.job, {
          jobType: val.jobType,
          jobStatus: val.jobStatus,
          currentStep: val.currentStep,
          totalSteps: val.totalSteps,
          succeeded: val.succeeded,
          failed: val.failed,
          updatedAt: val.updatedAt !== undefined && val.updatedAt !== null ? val.updatedAt : this.job.updatedAt,
          createdAt: val.createdAt !== undefined && val.createdAt !== null ? val.createdAt : this.job.createdAt,
        })
        jobsUiDebugLog("JobItem:initialJob_watch", {
          id: this.id,
          before: before,
          after: {
            succeeded: this.job.succeeded,
            failed: this.job.failed,
            jobStatus: this.job.jobStatus,
            currentStep: this.job.currentStep,
          },
        })
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.contents {
  display: flex;
}

.job-fail-link {
  font-weight: 700;
  text-decoration: underline;
}

pre {
  text-align: left;
  overflow: scroll;
  margin: 20px;
}
</style>
